import {AvalancheReelPartAnimator} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheReelPartAnimator";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Ease, Elastic, Linear, Timeline, TimelineLite, TweenLite} from "gsap";
import {FITHGameConfig} from "../FITHGameConfig";
import {ReelAnimationState} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelAnimationState";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {ReelPartAnimator} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelPartAnimator";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {SpinReelEvent} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {FITHGameModuleConfig} from "../FITHGameModuleConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {FITHSoundManager} from "../sound/FITHSoundManager";
import {FITHSoundConfig} from "../FITHSoundConfig";
import {StageEvent} from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";

export class FITHAvalancheReelPartAnimator extends AvalancheReelPartAnimator {
    private _currentTween: TweenLite | null;
    private _bouncingTargetReached: boolean;
    private _nearWin: boolean;
    private _prevReelId: number = -1; // #226
    private _nearWinBounce: TweenLite;
    private _newSymbols: StateReelSymbol[];
    private _symLandingWoodSoundPlayed: boolean = false;
    private _scatterSoundsPlayed: boolean = false;

    /**
     * @constructs
     * @param reelId
     */
    constructor(reelId: number) {
        super(reelId);

        CustomBounce.create("dropBounce", {strength: 0.15, squash: 0, squashID: "drop"});
        CustomBounce.create("shakeBounce", {strength: 0.8, squash: 0, endAtStart: true, squashID: "shake"});

        EventHandler.addEventListener(this, StageEvent.STAGE_RESIZED, () => this.onStageResized());
    }

    private _mode: string = "";

    set mode(value: string) {
        this._mode = value;
    }

    protected _spinSpeedsQueue: number[];

    public get spinSpeedsQueue(): number[] {
        return this._spinSpeedsQueue;
    }

    private _currentSpeed: number = 0;

    public get currentSpeed(): number {
        return this._currentSpeed;
    }

    public get isQuickStop(): boolean {
        return this._isQuickStop;
    }

    public set isQuickStop(value: boolean) {
        this._isQuickStop = value;
        if (this._isQuickStop) {
            if (this._mode === FITHGameMode.BONUS) {
                if (this._spinSpeedsQueue && this._spinSpeedsQueue.length > 0) {
                    this._spinSpeedsQueue.length = 0;
                }
            } else {
                this._stopSymDelay = AvalancheReelPartAnimator._quickStopSymbolDelay[this._reelId];
            }
            this.stopSpeed = ReelPartAnimator._spinQuickStopSpeeds[this._reelId];
        }
    }

    private _preYPos: number;

    public get preYPos(): number {
        return this._preYPos;
    }

    private _yPos: number;

    public get yPos(): number {
        return this._yPos;
    }

    public set yPos(value: number) {
        if (value === this._yPos) {
            return;
        }

        this._preYPos = this._yPos;
        this._yPos = value;
        const y: number = MathHelper.floorToDecimals(value, 2);
        for (let i: number = 0; i < this._symbolsToAnimate.length; i++) {
            this._symbolsToAnimate[i].y = MathHelper.floorToDecimals((this._targetPositions[i] + y), 2);
        }
    }

    public get currentStepLeftTime(): number {
        if (this._currentTween != null && this._currentTween.isActive()) {
            return this._currentSpeed * (1 - this._currentTween.progress());
        }
        return 0;
    }

    private _currentState: string;

    set currentState(value: string) {
        this._currentState = value;
    }

    private _isLastFs: boolean;

    set isLastFs(value: boolean) {
        this._isLastFs = value;
    }

    // used in bonus game only.
    private static getSpinStopEase(): Ease {
        const config: [number, number] = FITHGameConfig.instance.REEL_SPIN_STOP_ELASTIC_CONFIG_BONUS_GAME;
        return Elastic.easeOut.config(config[0], config[1]);
    }

    // used in bonus game only.
    private static getSpinQuickStopEase(): Ease {
        const configQuick: [number, number] = FITHGameConfig.instance.REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG_BONUS_GAME;
        return Elastic.easeOut.config(configQuick[0], configQuick[1])
    }

    /**
     * @inheritDoc
     * @override To reset the NEAR_WIN_NON_D_REEL_COUNT constant.
     * @param isFastSpin
     */
    public resetStartSpin(isFastSpin: boolean) {
        if (this._reelId === 0) {
            FITHGameConfig.instance.NEAR_WIN_NON_D_REEL_COUNT = 0;
        }

        this._prevReelId = -1;
        this._symLandingWoodSoundPlayed = false;
        this._scatterSoundsPlayed = false;

        super.resetStartSpin(isFastSpin);
    }

    // used in bonus game only.
    public getStartSpinAnimate(delay: number, newSymbol: StateReelSymbol): Timeline {
        if (this._isLastFs) {
            FITHGameModuleConfig.bonusGameVSymbolOverlayView.toggleHighlightSlot(this._reelId, true);
        }
        this._symbolsToAnimate.unshift(newSymbol);
        this.resetSymbolsPosition();
        const speed: number = FITHGameConfig.instance.REEL_SPIN_START_SPEED_BONUS_GAME;
        this._currentTween = TweenLite.to(this, speed, {
            ease: FITHGameConfig.instance.REEL_SPIN_START_EASE_BONUS_GAME,
            yPos: this.symStep
        });
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            this._currentTween,
            () => {
                this._currentSpeed = speed;
                this.fadeInBlurSymbols();
            }
        ], delay);
        return timeline;
    }

    // used in bonus game only.
    public getSpinAnimation(newSymbol: StateReelSymbol): Timeline {
        this._symbolsToAnimate.unshift(newSymbol);
        this.resetSymbolsPosition();
        const speed: number = this.calculateSpinSpeed();
        this._currentTween = TweenLite.to(this, speed, {
            ease: Linear.easeNone,
            yPos: this.symStep
        });
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            this._currentTween,
            () => this._currentSpeed = speed
        ]);
        return timeline;
    }

    // used in bonus game only.
    public getStopTime(stopSteps: number): number {
        // this is the stop time we know before stop. if there is slow motion spin happens during stopping, we add the delay when stop at onStopDelayDone().
        const spinSpeed: number = this.spinSpeed;
        return spinSpeed * stopSteps + this.stopSpeed;
    }

    // used in bonus game only.
    public getStopSpinAnimation(newSymbol: StateReelSymbol): Timeline {
        FITHGameModuleConfig.bonusGameVSymbolOverlayView.toggleHighlightSlot(this._reelId, false);

        this._symbolsToAnimate.unshift(newSymbol);
        this.resetSymbolsPosition();
        this._bouncingTargetReached = false;
        const speed = this.calculationStopSpinSpeed();
        this._currentTween = TweenLite.to(this, speed, {
            ease: this.isQuickStop ? FITHAvalancheReelPartAnimator.getSpinQuickStopEase() : FITHAvalancheReelPartAnimator.getSpinStopEase(),
            yPos: this.symStep,
            onUpdate: () => this.onBouncingUpdate()
        });
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            this._currentTween,
            () => {
                this._currentSpeed = speed;
                this.fadeOutSymbolBlur();
            }
        ]);
        timeline.add(() => this.onSpinStopCompleted());
        return timeline;
    }

    // used in bonus game only.
    public enterFS(): void {
        this.initTargetPositionsBonusGame();
        this.initSymbolsPositionBonusGame();
    }

    // used in bonus game only.
    public exitFS(): void {
        super.initTargetPositions();
        super.initSymbolsPosition();

        this._isLastFs = false;
    }

    // #209
    public getAllOutAnimation(delay: number, onSymbolCompleteCallback: (symbol: StateReelSymbol) => void): Timeline {
        if (this._reelId === 0) {
            FITHSoundManager.playReelsOut();
        }
        return super.getAllOutAnimation(delay, onSymbolCompleteCallback);
    }

    // #209
    public getNewSymbolsInAnimation(newSymbols: StateReelSymbol[], nearWinPositions: boolean[]): Timeline {
        if (this._reelId === 0 && this._currentState === ReelAnimationState.ALL_IN) {
            FITHSoundManager.playReelsIn();
        }

        this._newSymbols = [...newSymbols];
        return super.getNewSymbolsInAnimation(newSymbols, nearWinPositions);
    }

    /**
     * @inheritDoc
     * @override To give the stopping ease for avalanche.
     */
    protected getDefaultStopEase(): any {
        return "dropBounce";
    }

    /**
     * @inheritDoc
     * @override To spin X and D symbols all at once for ALL_IN case and therefore handle near win for non D/X symbols for ALL_IN case.
     * @param symbol
     * @param timeline
     * @param delay
     * @param targetPosition
     * @param firstSymbol
     * @param nearWin
     * @param triggerAttention
     */
    protected addSymbolInAnimationToTimeline(symbol: StateReelSymbol, timeline: Timeline, delay: number, targetPosition: number, firstSymbol: boolean, nearWin: boolean, triggerAttention: boolean): number {
        // handling spin of D and X symbols.
        if ((symbol.symName.indexOf("D") > -1 || symbol.symName.indexOf("X") > -1) && this._currentState === ReelAnimationState.ALL_IN) {
            // this check is making sure that in case of respin/avalanche mode, it should return back without doing any thing.
            if (symbol.y === targetPosition) {
                return delay;
            }

            symbol.y += (2 * FITHGameConfig.instance.REEL_AREA_HEIGHT);

            timeline.add(TweenLite.to(symbol, this.stopSpeed, { // comment from parent method, I don't abstract the time out here because we should override the whole animation function.
                ease: Linear.easeNone,
                y: targetPosition,
                onComplete: () => {
                    // #209
                    if (this._reelId === 0 && firstSymbol) {
                        FITHSoundManager.playSymLandingAll();
                    }
                    if (triggerAttention) {
                        this.onSymbolInAttentionPointReached(symbol);
                    }
                }
            }), 0);
            const unBlurSymbolTimeline: TimelineLite = new TimelineLite({paused: true});
            unBlurSymbolTimeline.add(symbol.changeState({
                state: FITHGameConfig.instance.SYMBOL_STATES.normal,
                replay: false,
                setStack: true,
                fadeInDuration: FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION,
                fadeInDelay: FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY
            }));
            timeline.add(() => unBlurSymbolTimeline.play(), 0);
            timeline.add(() => {}, unBlurSymbolTimeline.totalDuration());

            return 0;
        }

        // #166
        // make 5th scatter landing reel symbols'(other than bonus sym) nearWin false, no need to anticipate them.
        if (this._reelId === FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID && symbol.symName !== ReelSymbolName.BONUS) {
            nearWin = false;
        }

        // handling near win for non D/X symbols and, #166 -> also the case when it reaches the limit of maxScatters which is 5.
        if (!this._isQuickStop) {
            const nearWinDelay: number = FITHGameConfig.instance.NEAR_WIN_DELAY;

            if (nearWin) {
                // #226
                if (this._prevReelId !== this._reelId) {
                    FITHGameConfig.instance.NEAR_WIN_NON_D_REEL_COUNT++;
                    this._prevReelId = this._reelId;
                }
                delay = (nearWinDelay * FITHGameConfig.instance.NEAR_WIN_NON_D_REEL_COUNT);
            } else if (this._reelId >= FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID) {
                delay = ((nearWinDelay * FITHGameConfig.instance.NEAR_WIN_NON_D_REEL_COUNT) + 0.2);
            }
        }

        this._nearWin = nearWin;

        return super.addSymbolInAnimationToTimeline(symbol, timeline, delay, targetPosition, firstSymbol, nearWin, triggerAttention);
    }

    protected onSymbolInAttentionPointReached(symbol: StateReelSymbol): void {
        super.onSymbolInAttentionPointReached(symbol);

        // don't shake if it already shaking
        if (this._nearWinBounce && this._nearWinBounce.totalProgress() > 0 && this._nearWinBounce.totalProgress() < 1) {
            return;
        }

        if (symbol.symName !== "D" && symbol.symName !== "X" && this._nearWin && !this._isQuickStop) {
            // #209, #227, #262
            let shakeOffset: number = 15,
                scatterFound: boolean = false;

            if (this._newSymbols.length === 1) {
                if (this._newSymbols[0].symName === "B") {
                    scatterFound = true;
                } else {
                    FITHSoundManager.playSymLandingWoodSingle();
                }
            } else {
                if (!this._symLandingWoodSoundPlayed) {
                    this._symLandingWoodSoundPlayed = true;
                    FITHSoundManager.playSymLandingWood();
                }

                for (let i: number = 0; i < this._newSymbols.length; i++) {
                    if (this._newSymbols[i].symName === "B") {
                        scatterFound = true;

                        break;
                    }
                }
            }

            if (scatterFound && !this._scatterSoundsPlayed) {
                this._scatterSoundsPlayed = true;
                FITHSoundManager.playScatterLands();
                FITHSoundManager.playScatterWin();

                shakeOffset = 30;
            }

            TweenLite.killTweensOf(StageManager.stage);
            StageManager.stage.y = FITHGameConfig.instance.STAGE_INIT_Y;

            this._nearWinBounce = TweenLite.to(StageManager.stage, 0.2, {y: "+=" + shakeOffset, ease: "shakeBounce"});
        }
    }

    private onStageResized(): void {
        if (this._nearWinBounce && this._nearWinBounce.isActive()) {
            this._nearWinBounce.progress(1);

            StageManager.stage.y = FITHGameConfig.instance.STAGE_INIT_Y;
        }
    }

    // ---- Following section is used only in bonus game. ----

    private calculateSpinSpeed(): number {
        return (!this.isQuickStop && this._spinSpeedsQueue && this._spinSpeedsQueue.length > 0) ? this._spinSpeedsQueue.shift()! : this.spinSpeed;
    }

    private fadeInBlurSymbols(): void {
        for (let i: number = 0; i < this._symbolsToAnimate.length; i++) {
            this._symbolsToAnimate[i].changeState({
                state: FITHGameConfig.instance.SYMBOL_STATES.spin,
                replay: false,
                fadeInDuration: FITHGameConfig.instance.REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION,
                fadeInDelay: FITHGameConfig.instance.REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY
            });
        }
    }

    private resetSymbolsPosition(): void {
        this.yPos = 0;
        this._preYPos = 0;
    }

    private calculationStopSpinSpeed(): number {
        return (!this.isQuickStop && this._spinSpeedsQueue && this._spinSpeedsQueue.length > 0) ? this._spinSpeedsQueue.shift()! : FITHGameConfig.instance.REEL_SPIN_STOP_SPEED;
    }

    private onBouncingUpdate(): void {
        if (this._bouncingTargetReached) {
            return;
        }

        if (this._yPos < this._preYPos) {
            this.onBouncingTargetReached();
        }
    }

    private onBouncingTargetReached(): void {
        this._bouncingTargetReached = true;
        EventHandler.dispatchEvent(new GameEvent(SpinReelEvent.REEL_STOP_SPIN_BOUNCE_STARTED, {
            reelId: this._reelId,
            quickStop: this.isQuickStop,
            fastSpin: this.isFastSpin
        }));
    }

    private fadeOutSymbolBlur(): void {
        let sym: StateReelSymbol;

        // #278
        if (this._reelId === FITHGameModuleConfig.spinController.getFirstActiveReelInBonus()) {
            TweenLite.delayedCall(0.2, () => FITHSoundManager.playFSReelsLanding());
        }

        if (this._reelId === FITHGameModuleConfig.spinController.getLastActiveReelInBonus()) {
            TweenLite.delayedCall(0.2, () => {
                // #231
                FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_REELS_SPINNING);

                // #278
                FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_REELS_LANDING);
            });
        }

        for (let i: number = 0; i < (this._symbolsToAnimate.length - 1); i++) {
            sym = this._symbolsToAnimate[i];
            if (sym.symName === "V") {
                FITHGameModuleConfig.bonusGameVSymbolOverlayView.triggerLandAnimation(this._reelId, (this.isQuickStop || GameSetting.isFastSpin));
            }
            this._symbolsToAnimate[i].changeState({
                state: FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_STATE ? FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_STATE() : FITHGameConfig.instance.SYMBOL_STATES.normal,
                replay: false,
                fadeInDuration: FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION,
                fadeInDelay: FITHGameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY
            });
        }
    }

    private onSpinStopCompleted(): void {
        this._currentSpeed = 0;
        this._currentTween = null;
        if (this._spinSpeedsQueue && this._spinSpeedsQueue.length > 0) {
            this._spinSpeedsQueue.length = 0;
        }

        if (!this._bouncingTargetReached) {
            this.onBouncingTargetReached();
        }
    }

    private initTargetPositionsBonusGame(): void {
        this._targetPositions = [];
        let targetPosition: number;
        const positionsNumTotal: number = ArrayHelper.arraySum(FITHGameConfig.instance.SYMBOLS_NUM_IN_REEL);
        for (let i: number = 0; i < positionsNumTotal; i++) {
            targetPosition = (this.symStep * i) - this._symHeight / 2;
            this._targetPositions.push(targetPosition);
        }
    }

    private initSymbolsPositionBonusGame(): void {
        this._yPos = 0;
        this._preYPos = 0;
        this.yPos = this.symStep;
    }
}